import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { printTicket80mm } from '../utils/ticket80mmPrint';
import Layout from '../components/Layout';
import { Plus, Trash2, Calculator, Printer, FileText, CreditCard } from 'lucide-react';
import { BrandSelector, ModelSelector, useBrandsModels } from '../components/BrandModelSelector';
import SupplierPartSearch from '../components/SupplierPartSearch';

const createItem = () => ({
  id: `item-${Date.now()}-${Math.random()}`,
  description: '',
  reference: '',
  prix_achat: ''
});

const CalculPage = () => {
  const { api, isAdmin } = useAuth();
  const { t, tError, formatCurrency } = useLanguage();
  const [items, setItems] = useState([createItem()]);
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const { brands: allBrands, modelsMap: brandModelsMap, seriesMap: brandSeriesMap } = useBrandsModels(api);
  const [formData, setFormData] = useState({
    docType: 'DEVIS',
    clientNom: '',
    clientTel: '',
    clientEmail: '',
    quantite: 1,
    mainOeuvre: 0,
    refPiece: '',
    fournisseur: '',
    saveClient: false,
    acceptOnlinePayment: false
  });
  const [previewUrl, setPreviewUrl] = useState('');
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [docId, setDocId] = useState('');
  const [docRef, setDocRef] = useState('');
  const [clientSuggestions, setClientSuggestions] = useState([]);

  const payload = useMemo(() => ({
    items: items.map(item => ({
      description: item.description,
      prix_achat: parseFloat(item.prix_achat) || 0,
      reference: item.reference
    })),
    quantite: parseInt(formData.quantite, 10) || 1,
    main_oeuvre: parseFloat(formData.mainOeuvre) || 0,
    client_nom: formData.clientNom,
    client_tel: formData.clientTel,
    client_email: formData.clientEmail,
    ref_piece: formData.refPiece,
    fournisseur: formData.fournisseur,
    doc_type: formData.docType,
    save_client: formData.saveClient,
    accept_online_payment: formData.acceptOnlinePayment
  }), [items, formData]);

  const addItem = () => setItems(prev => [...prev, createItem()]);

  const removeItem = (id) => {
    if (items.length === 1) {
      // Reset the last remaining item instead of removing it
      setItems([createItem()]);
      return;
    }
    setItems(prev => prev.filter(item => item.id !== id));
  };

  const updateItem = (id, field, value) => {
    setItems(prev => prev.map(item => (item.id === id ? { ...item, [field]: value } : item)));
  };

  const openBlobForPrint = (blob) => {
    const url = window.URL.createObjectURL(blob);
    const printWindow = window.open(url, '_blank', 'width=900,height=700');
    if (!printWindow) {
      alert(t('calcul.alertAllowPopups'));
      return;
    }
    const triggerPrint = () => {
      printWindow.focus();
      printWindow.print();
    };
    printWindow.addEventListener('load', triggerPrint);
    setTimeout(triggerPrint, 1200);
    setTimeout(() => window.URL.revokeObjectURL(url), 8000);
  };

  const fetchDetail = async () => {
    try {
      const response = await api.post('/calcul/prix', payload);
      setDetail(response.data);
    } catch (error) {
      console.error('Erreur calcul:', error);
    }
  };

  const handleGenerate = async (action) => {
    const hasValidItem = payload.items.some(item => item.description && item.prix_achat > 0);
    if (!hasValidItem) {
      alert(t('calcul.alertAddItem'));
      return;
    }

    setLoading(true);
    try {
      const response = await api.post(`/calcul/pdf?action=${action}`, payload, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
      setDocId(response.headers['x-calcul-id'] || '');
      setDocRef(response.headers['x-calcul-ref'] || '');
      await fetchDetail();
      if (action === 'save') {
        alert(t('calcul.ticketSaved'));
      }
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('calcul.errorGenerateTicket'));
    } finally {
      setLoading(false);
    }
  };

  const handlePrintTicket = async () => {
    const hasValidItem = payload.items.some((item) => item.description && item.prix_achat > 0);
    if (!hasValidItem) {
      alert(t('calcul.alertAddItem'));
      return;
    }
    try {
      await printTicket80mm(api, { type: 'calcul', payload });
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('calcul.errorPrintTicket'));
    }
  };

  const handleOpenA4 = async () => {
    try {
      if (docId) {
        const response = await api.get(`/calcul/documents/${docId}/a4`, { responseType: 'blob' });
        openBlobForPrint(response.data);
        return;
      }
      const response = await api.post('/calcul/a4', { ...payload, reference_doc: docRef || undefined }, { responseType: 'blob' });
      openBlobForPrint(response.data);
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('calcul.errorGenerateA4'));
    }
  };

  useEffect(() => {
    if (!formData.clientNom || formData.clientNom.length < 2) {
      setClientSuggestions([]);
      return;
    }
    const timeout = setTimeout(async () => {
      try {
        const response = await api.get(`/clients/autocomplete/${encodeURIComponent(formData.clientNom)}`);
        setClientSuggestions(response.data);
      } catch (error) {
        console.error('Erreur autocomplete:', error);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [formData.clientNom]);

  const handleSelectClient = (client) => {
    setFormData(prev => ({
      ...prev,
      clientNom: client.nom || '',
      clientTel: client.telephone || '',
      clientEmail: client.email || ''
    }));
    setClientSuggestions([]);
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-6 fade-in" data-testid="calcul-page">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-cyan-500/10 text-cyan-400">
            <Calculator className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-white">{t('calcul.title')}</h2>
            <p className="text-sm text-gray-400">{t('calcul.subtitle')}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[1.4fr_1fr] gap-6">
          <div className="glass p-6 space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-cyan-400">{t('calcul.clientSection')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                  <label className="block text-sm text-gray-300 mb-1">{t('calcul.clientName')}</label>
                  <input
                    type="text"
                    value={formData.clientNom}
                    onChange={(e) => setFormData(prev => ({ ...prev, clientNom: e.target.value }))}
                    className="input-dark w-full py-2"
                    data-testid="calcul-client-nom"
                  />
                  {clientSuggestions.length > 0 && (
                    <div className="absolute left-0 right-0 mt-1 bg-black/90 border border-white/10 rounded-lg z-20">
                      {clientSuggestions.map((client) => (
                        <button
                          key={client.id}
                          type="button"
                          onClick={() => handleSelectClient(client)}
                          className="w-full text-left px-3 py-2 text-sm text-gray-200 hover:bg-white/10"
                          data-testid={`calcul-client-suggestion-${client.id}`}
                        >
                          {client.nom} · {client.telephone}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">{t('calcul.phone')}</label>
                  <input
                    type="tel"
                    value={formData.clientTel}
                    onChange={(e) => setFormData(prev => ({ ...prev, clientTel: e.target.value }))}
                    className="input-dark w-full py-2"
                    data-testid="calcul-client-tel"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">{t('calcul.email')}</label>
                  <input
                    type="email"
                    value={formData.clientEmail}
                    onChange={(e) => setFormData(prev => ({ ...prev, clientEmail: e.target.value }))}
                    className="input-dark w-full py-2"
                    data-testid="calcul-client-email"
                  />
                </div>
                <div className="flex items-center gap-2 mt-7">
                  <input
                    type="checkbox"
                    checked={formData.saveClient}
                    onChange={(e) => setFormData(prev => ({ ...prev, saveClient: e.target.checked }))}
                    className="w-4 h-4"
                    data-testid="calcul-save-client"
                  />
                  <span className="text-sm text-gray-300">{t('calcul.saveClient')}</span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-bold text-cyan-400">{t('calcul.deviceSection')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">{t('calcul.brand')}</label>
                  <BrandSelector
                    value={brand}
                    onChange={v => { setBrand(v); setModel(''); }}
                    brands={allBrands}
                    testId="calcul-brand"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">{t('calcul.model')}</label>
                  <ModelSelector
                    value={model}
                    onChange={v => setModel(v)}
                    models={brandModelsMap[brand] || []}
                    series={brandSeriesMap[brand] || {}}
                    brand={brand}
                    testId="calcul-model"
                  />
                </div>
              </div>
              {brand && model && (
                <div className="relative z-[90]">
                  <SupplierPartSearch
                    brand={brand}
                    model={model}
                    api={api}
                    onSelect={(part) => {
                      // Find the first empty item and fill it, or add a new one
                      const emptyIndex = items.findIndex(item => !item.description && !item.prix_achat);
                      if (emptyIndex >= 0) {
                        updateItem(items[emptyIndex].id, 'description', part.description);
                        updateItem(items[emptyIndex].id, 'reference', part.reference);
                        updateItem(items[emptyIndex].id, 'prix_achat', part.prix_achat);
                      } else {
                        const newItem = createItem();
                        newItem.description = part.description;
                        newItem.reference = part.reference;
                        newItem.prix_achat = part.prix_achat;
                        setItems(prev => [...prev, newItem]);
                      }
                      setFormData(prev => ({ ...prev, fournisseur: part.fournisseur, refPiece: part.reference }));
                    }}
                  />
                </div>
              )}
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-bold text-cyan-400">{t('calcul.itemsSection')}</h3>
              {items.map((item, index) => (
                <div key={item.id} className="grid grid-cols-1 md:grid-cols-[1.6fr_0.8fr_0.6fr_auto] gap-3 items-end">
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">{t('calcul.itemDescription')}</label>
                    <input
                      type="text"
                      value={item.description}
                      onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                      className="input-dark w-full py-2"
                      data-testid={`calcul-item-description-${index}`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">{t('calcul.itemReference')}</label>
                    <input
                      type="text"
                      value={item.reference}
                      onChange={(e) => updateItem(item.id, 'reference', e.target.value)}
                      className="input-dark w-full py-2"
                      data-testid={`calcul-item-reference-${index}`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">{t('calcul.itemPurchasePrice')}</label>
                    <input
                      type="number"
                      value={item.prix_achat}
                      onChange={(e) => updateItem(item.id, 'prix_achat', e.target.value)}
                      className="input-dark w-full py-2"
                      step="0.01"
                      min="0"
                      data-testid={`calcul-item-prix-${index}`}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeItem(item.id)}
                    className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30"
                    data-testid={`calcul-item-remove-${index}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                onClick={addItem}
                className="w-full py-2 rounded-xl border-2 border-dashed border-white/15 text-gray-300 hover:border-cyan-500/60 hover:text-cyan-400 transition-colors flex items-center justify-center gap-2 text-sm"
                data-testid="calcul-add-item"
              >
                <Plus className="w-4 h-4" />
                {t('calcul.addLine')}
              </button>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-bold text-cyan-400">{t('calcul.paramsSection')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">{t('calcul.quantity')}</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.quantite}
                    onChange={(e) => setFormData(prev => ({ ...prev, quantite: e.target.value }))}
                    className="input-dark w-full py-2"
                    data-testid="calcul-quantite"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">{t('calcul.labor')}</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.mainOeuvre}
                    onChange={(e) => setFormData(prev => ({ ...prev, mainOeuvre: e.target.value }))}
                    className="input-dark w-full py-2"
                    data-testid="calcul-main-oeuvre"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">{t('calcul.partReference')}</label>
                  <input
                    type="text"
                    value={formData.refPiece}
                    onChange={(e) => setFormData(prev => ({ ...prev, refPiece: e.target.value }))}
                    className="input-dark w-full py-2"
                    data-testid="calcul-ref-piece"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">{t('calcul.supplier')}</label>
                  <input
                    type="text"
                    value={formData.fournisseur}
                    onChange={(e) => setFormData(prev => ({ ...prev, fournisseur: e.target.value }))}
                    className="input-dark w-full py-2"
                    data-testid="calcul-fournisseur"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm text-gray-300 mb-1">{t('calcul.docType')}</label>
                  <select
                    value={formData.docType}
                    onChange={(e) => setFormData(prev => ({ ...prev, docType: e.target.value }))}
                    className="input-dark w-full py-2"
                    data-testid="calcul-doc-type"
                  >
                    <option value="DEVIS">{t('calcul.docTypeDevis')}</option>
                    <option value="FACTURE">{t('calcul.docTypeFacture')}</option>
                    <option value="PROFORMA">{t('calcul.docTypeProforma')}</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-blue-500/5 border border-blue-500/20">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={formData.acceptOnlinePayment}
                        onChange={(e) => setFormData(prev => ({ ...prev, acceptOnlinePayment: e.target.checked }))}
                        className="w-4 h-4 rounded accent-blue-500"
                        data-testid="calcul-accept-online-payment"
                      />
                      <div>
                        <span className="text-sm text-gray-300">{t('calcul.acceptOnlinePayment')}</span>
                        <p className="text-[11px] text-gray-500">{t('calcul.acceptOnlinePaymentHint')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => handleGenerate('save')}
                disabled={loading}
                className="flex-1 py-3 rounded-xl bg-cyan-500 hover:bg-cyan-600 text-white font-semibold disabled:opacity-60"
                data-testid="calcul-save-ticket"
              >
                {loading ? t('calcul.saving') : t('calcul.saveTicket')}
              </button>
              <button
                onClick={() => handleGenerate('preview')}
                disabled={loading}
                className="flex-1 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold"
                data-testid="calcul-preview-ticket"
              >
                {t('calcul.preview')}
              </button>
            </div>
          </div>

          <div className="glass p-6 space-y-4">
            <h3 className="text-lg font-bold text-cyan-400">{t('calcul.previewSection')}</h3>
            {previewUrl ? (
              <iframe
                title={t('calcul.previewIframeTitle')}
                src={previewUrl}
                className="w-full h-[520px] rounded-xl border border-white/10"
                data-testid="calcul-preview-iframe"
              />
            ) : (
              <div className="text-gray-400 text-center py-20" data-testid="calcul-preview-empty">
                {t('calcul.previewEmpty')}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <button
                onClick={handlePrintTicket}
                className="px-4 py-2 rounded-xl bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 text-sm font-semibold"
                data-testid="calcul-print-ticket"
                disabled={!previewUrl}
              >
                <Printer className="inline w-4 h-4 mr-2" />
                {t('calcul.printTicket')}
              </button>
              <button
                onClick={handleOpenA4}
                className="px-4 py-2 rounded-xl bg-indigo-500/20 text-indigo-300 hover:bg-indigo-500/30 text-sm font-semibold"
                data-testid="calcul-open-a4"
                disabled={!previewUrl}
              >
                <FileText className="inline w-4 h-4 mr-2" />
                {t('calcul.transformA4')}
              </button>
            </div>

            {detail && (
              <div className="space-y-2 text-sm text-gray-200" data-testid="calcul-detail">
                <div className="flex justify-between">
                  <span>{t('calcul.totalArticles')}</span>
                  <span>{formatCurrency(detail.detail.total_articles)}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('calcul.totalPurchase')}</span>
                  <span>{formatCurrency(detail.detail.total_achat)}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('calcul.shippingFees')}</span>
                  <span>{formatCurrency(detail.detail.frais_envoi)}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('calcul.customsFees')}</span>
                  <span>{formatCurrency(detail.detail.frais_douane)}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('calcul.costPerPiece')}</span>
                  <span>{formatCurrency(detail.detail.cout_par_piece)}</span>
                </div>
                <div className="flex justify-between font-bold text-cyan-300">
                  <span>{t('calcul.finalPrice')}</span>
                  <span>{formatCurrency(detail.prix_final)}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CalculPage;
